import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, Inject } from '@angular/core';
import { PickerItem } from '../../models/picker-item.model';
import { FormField } from '../../models/form-builder/form-field.model';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { FormInstanceElement } from '../../models/form-builder/form-instance-element.model';
import { FormRendererComponent } from '../../components/form-renderer/form-renderer.component';
import { FormFieldPropertyEnum } from '../../models/form-builder/form-field-property-enum.model';
import { FormInstanceHelper } from '../../form-instance.helper';

export class AddContactData {
    public selectedUser: PickerItem;
    public isPrimaryConact: boolean;
    // If the Contact Grid has been configured with additional fields (beyond the standard ones of Name, EMail etc)
    // the fields' associated FormInstanceElements are stored here
    public extraFieldFormInstanceElements: FormInstanceElement[];

    public constructor(selectedUser: PickerItem, extraFieldFormInstanceElements: FormInstanceElement[], isPrimaryConact: boolean = false) {
        this.selectedUser = selectedUser;
        this.extraFieldFormInstanceElements = extraFieldFormInstanceElements;
        this.isPrimaryConact = isPrimaryConact;
    }
}

/*
    PJH - 2/4/2026
    For VNEXT-2084 (enforcing required fields) in the dialog and in the Contacts Grid substantial updates were made
    Two kinds of field have to be handled:
    - "standard" contacts fields (Name, Email, Phone etc)
    - "extra" fields which can be added above and beyond the standard ones
    Now, both kinds of field have to be valid before the user can click ok and add a row to the Contacts Grid

    I dealt with the two kinds of field separately...

    Standard Fields:
    - Implemented originally as a special case. They're distinct fields in the HTML.
    - I left them this way to make it easy to populate them from the "Add User" picker

    Extra Fields:
    - Rendered using our existing <app-form-renderer> component (used for Forn Builder, Form Instances, SHP)
    - This was done because validation, flex field logic, conditional logic etc are all built in
    - Doing this required building a dummy FormInstance to pass to as Input to the FormRenderer so it can do its thing
    - See https://community-dc.max.gov/x/XQSOoQ
*/

@Component({
    selector: 'app-add-contact',
    templateUrl: './add-contact.dialog.html',
    styleUrl: './add-contact.dialog.scss',
    standalone: false
})
export class AddContactDialog {
    private selectedUser: PickerItem;
    private requiredFields: FormField[];
    private extraFields: FormField[];

    public isPrimaryContact: boolean = false;
    // If there are any extra fields and any are invalid, this gets set to true to prevent click ok
    private hasInvalidExtraFields: boolean;
    // The dialog renders any extra fields using an <app-form-renderer> which takes a FormInstance
    // a dummy FormInstance (rather than an "actual" one, stored in the DB) is created for this purpose
    private dummyFormInstance: FormInstance;
    // A reference to the FormRenderer is needed so that when closing the dialog its CheckFormValidations()
    // method can be called 
    private formRenderer: FormRendererComponent;

    public constructor(
        @Inject(MAT_DIALOG_DATA) data,
        public dialogRef: MatDialogRef<AddContactDialog>,) {
        this.requiredFields = data.fields.filter(x => { return x.required });

        // Extra fields are identified by not having "donotedit" in their internal name. This is because all of the
        // standard fields (Name, Email etc) are given names with this suffix, such as contactnamedonotedit, contactemaildonotedit etc
        this.extraFields = data.fields.filter(x => { return x.name.indexOf('donotedit') == -1 }); 
        // If a user pickerItem was provided, use it to set the user
        if (data.pickerItem) {
            this.onUserSet(data.pickerItem);
        }

        // If there are more fields than the standard ones, build a dummy FormInstance from those extra fields
        // to be passed to the <app-form-renderer> that will be used in the HTML to display them and manage their
        // validity
        if (this.extraFields.length > 0) {
            this.dummyFormInstance = FormInstanceHelper.createDummyFormInstanceFromFields(this.extraFields, data.formInstance);
        }
    }

    public onUserSet(user: PickerItem) {
        this.initSelectedUser(user);
    }

    // Look up the field by displayName in array of required fields built in the constructor
    public isRequired(fieldName: string): boolean {
        const field = this.requiredFields.find(x => { return x.displayName == fieldName });
        if (field)
            return true;
        else
            return false;
    }

    // It proved not possible to use a ViewChild to reference the FormRenderer (due to errors resulting from circular references)
    // and so as a work around we handle FormRenderer's onInit event by storing a reference it for later use
    public handleFormRendererInitialized(renderer: FormRendererComponent): void {
        this.formRenderer = renderer;
    }

    // This is needed to ensure that edits made within the FormRenderer that result in validity changes
    // are used to set hasInvalidExtraFields correctly which impacts the disabled status of the OK button
    public handleValidityStatus(status: string): void {
        this.hasInvalidExtraFields = (status == FormFieldPropertyEnum.INVALID);
    }

    // If there are extra fields, validate them using the FormRenderer
    // The call to CheckFormValidations causes FormRenderer's validityStatus event
    // to fire, which is handled in handleValidityStatus() above
    public closeDialog(save: boolean) {
        if (save) {
            if (this.extraFields.length > 0) {
                this.formRenderer.CheckFormValidations().then(() => {
                    if (this.formRenderer.IsFormValid()) {
                        this.hasInvalidExtraFields = false;
                        this.dialogRef.close(new AddContactData(this.selectedUser, this.dummyFormInstance.formInstanceElements, this.isPrimaryContact));
                    } else {
                        this.hasInvalidExtraFields = true;
                    }
                });
            } else {
                this.dialogRef.close(new AddContactData(this.selectedUser, [], this.isPrimaryContact));
            }
        } else {
            this.dialogRef.close();
        }
    }

    // getters
    public get DummyFormInstance(): FormInstance {
        return this.dummyFormInstance;
    }
    public get HasInvalidExtraFields(): boolean {
        return this.hasInvalidExtraFields;
    }
    public get ExtraFields(): FormField[] {
        return this.extraFields;
    }
    public get ContactName(): string {
        return this.selectedUser?.displayName;
    }
    public set ContactName(value: string) {
        this.initSelectedUser();
        this.selectedUser.displayName = value;
    }

    public get Phone(): string {
        return this.selectedUser?.phone
    }
    public set Phone(value: string) {
        this.initSelectedUser();
        this.selectedUser.phone = value;
    }

    public get Email(): string {
        return this.selectedUser?.secondaryDisplayName;
    }
    public set Email(value: string) {
        this.initSelectedUser();
        this.selectedUser.secondaryDisplayName = value;
    }

    public get Agency(): string {
        return this.selectedUser?.agencyShortName;
    }
    public set Agency(value: string) {
        this.initSelectedUser();
        this.selectedUser.agencyShortName = value;
    }

    public get SelectedUser(): PickerItem {
        return this.selectedUser;
    }

    // initializes this.selectedUser
    // If a non-null user is passed it uses that, otherwise it initializes it to an empty PickerItem
    // but only if it's not already been initialized
    private initSelectedUser(user: PickerItem = null): void {
        if (user) {
            // Setting user ...
            this.selectedUser = user;
        } else if (!this.selectedUser) {
            // Setting to new, empty pickerItem ...
            this.selectedUser = new PickerItem({});
        }
    }
}
